import React, { FormEvent, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { api, errorMessage } from "./api";
import { ActivationLookupItem, QueryResult } from "./types";
import { formatActivationDate, lastActiveHelp } from "./activationDates";

export default function ActivationLookupPanel({ csrf, onOpenLicense, onOpenUser }: {
  csrf: string;
  onOpenLicense: (licenseId: string) => void;
  onOpenUser: (email: string) => void;
}) {
  const [name, setName] = useState("");
  const [query, setQuery] = useState<{ name: string; page: number } | null>(null);
  const [result, setResult] = useState<QueryResult<ActivationLookupItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!query) return;
    let active = true;
    setLoading(true);
    setResult(null);
    setError("");
    api.lookupActivations(query.name, query.page, csrf).then(data => {
      if (active) setResult(data);
    }).catch(requestError => {
      if (active) setError(errorMessage(requestError));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query, csrf]);

  const search = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim() && !loading) setQuery({ name: name.trim(), page: 1 });
  };

  return <Paper sx={{ p: { xs: 2, sm: 3 } }}>
    <Typography component="h2" variant="h5">Activation lookup</Typography>
    <Typography color="text.secondary" sx={{ mt: 1, mb: 2.5 }}>
      Find license and trial activations by all or part of a PC name. Names are not case-sensitive; each result shows its recorded PC ID.
    </Typography>
    <Stack component="form" onSubmit={search} direction={{ xs: "column", sm: "row" }} spacing={1.5}>
      <TextField id="support-pc-name" label="PC name" required fullWidth value={name} disabled={loading}
        onChange={event => setName(event.target.value)} inputProps={{ maxLength: 255 }} />
      <Button type="submit" variant="contained" startIcon={<SearchIcon />} disabled={!name.trim() || loading} sx={{ minWidth: 190 }}>Look up activations</Button>
    </Stack>
    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    {loading && <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={28} aria-label="Looking up activations" /></Box>}
    {result && <Box sx={{ mt: 3 }}>
      <Typography sx={{ mb: 2 }}>{result.total} activation{result.total === 1 ? "" : "s"} found for “{query?.name}”.</Typography>
      {result.items.length > 0 && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>Last active: {lastActiveHelp}</Typography>}
      {result.items.length === 0 ? <Alert severity="info">No activations found for this PC name.</Alert> : <TableContainer>
        <Table size="small" aria-label="PC activations" sx={{ minWidth: 800 }}>
          <TableHead><TableRow><TableCell>PC name</TableCell><TableCell>PC ID</TableCell><TableCell>OS</TableCell><TableCell>Product / Type</TableCell><TableCell>Last active</TableCell><TableCell>User</TableCell><TableCell>License</TableCell></TableRow></TableHead>
          <TableBody>{result.items.map(item => <TableRow key={`${item.activation_type}:${item.id}`}>
            <TableCell>{item.machine_name || "Unnamed system"}</TableCell>
            <TableCell sx={{ overflowWrap: "anywhere", maxWidth: 260 }}>{item.machine_id || "Not recorded"}</TableCell>
            <TableCell>{item.machine_os || "Unknown"}</TableCell>
            <TableCell>{item.product_id || "Unknown product"}<Typography variant="caption" sx={{ display: "block" }}>{item.activation_type === "trial" ? "Trial" : "License"}</Typography></TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{formatActivationDate(item.updated_at)}</TableCell>
            <TableCell>{item.owner_email ? <Button size="small" sx={{ overflowWrap: "anywhere" }} onClick={() => onOpenUser(item.owner_email!)}>{item.owner_email}</Button> : "—"}</TableCell>
            <TableCell>{item.license_id ? <Button size="small" sx={{ overflowWrap: "anywhere" }} onClick={() => onOpenLicense(item.license_id!)}>{item.license_id}</Button> : "—"}</TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </TableContainer>}
      <TablePagination component="div" count={result.total} page={result.page - 1} rowsPerPage={25} rowsPerPageOptions={[25]}
        onPageChange={(_event, page) => query && setQuery({ name: query.name, page: page + 1 })} />
    </Box>}
  </Paper>;
}
